import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { GraduationCap, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');

  const verifyToken = useCallback(async (verifyToken) => {
    try {
      const res = await axios.post(`${API}/auth/verify-email`, { token: verifyToken }, { withCredentials: true });
      setStatus('success');
      setMessage(res.data.message);
    } catch (err) {
      setStatus('error');
      const detail = err.response?.data?.detail;
      setMessage(typeof detail === 'string' ? detail : 'Verification failed. The link may be invalid or expired.');
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token found.');
      return;
    }
    verifyToken(token);
  }, [token, verifyToken]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-8 sm:p-10 text-center">
          <GraduationCap className="w-10 h-10 text-cabernet mx-auto mb-4" />

          {status === 'verifying' && (
            <>
              <Loader2 className="w-8 h-8 text-brass mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-heading font-medium text-cabernet mb-2">Verifying your email...</h1>
              <p className="font-body text-sm text-ink/60">Please wait a moment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-medium text-cabernet mb-2">Email Verified</h1>
              <p className="font-body text-sm text-ink/60 mb-6" data-testid="verify-success-message">{message}</p>
              <Link to="/dashboard">
                <Button className="bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm px-6 py-5" data-testid="go-to-dashboard-btn">
                  Go to Dashboard
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-medium text-cabernet mb-2">Verification Failed</h1>
              <p className="font-body text-sm text-ink/60 mb-6" data-testid="verify-error-message">{message}</p>
              <Link to="/login">
                <Button variant="outline" className="border-2 border-cabernet text-cabernet hover:bg-cabernet/5 font-accent text-sm" data-testid="back-to-login-btn">
                  Back to Sign In
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
